(() => {
  if (customElements.get('labchest-video-library')) return;
  class LabChestVideoLibrary extends HTMLElement {
    static instagramURL(value) {
      try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || !['instagram.com', 'www.instagram.com'].includes(url.hostname) || url.port || url.username || url.password) return null;
        const match = url.pathname.match(/^\/(reel|p)\/([A-Za-z0-9_-]+)\/?$/);
        if (!match) return null;
        const original = `https://www.instagram.com/${match[1]}/${match[2]}/`;
        return { original, embed: `${original}embed/` };
      } catch { return null; }
    }
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.states = new Map();
      this.order = 'oldest';
      this.sort = this.querySelector('[data-video-sort]');
      if (this.sort) {
        this.sort.value = this.order;
        this.querySelector('[data-video-sort-control]').hidden = false;
        this.sort.addEventListener('change', () => this.changeOrder(), { signal: this.controller.signal });
      }
      this.dialog = this.querySelector('[data-video-dialog]');
      this.addEventListener('click', event => this.onClick(event), { signal: this.controller.signal });
      this.dialog?.addEventListener('close', () => this.closePlayer(), { signal: this.controller.signal });
      this.dialog?.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const first = this.dialog.querySelector('[data-video-close]');
        const last = this.dialog.querySelector('[data-video-original]');
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }, { signal: this.controller.signal });
      this.dialog?.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, { signal: this.controller.signal });
      this.observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.isIntersecting) { this.observer.unobserve(entry.target); this.load(entry.target); } });
      }, { rootMargin: '250px' }) : null;
      this.querySelectorAll('[data-video-category]').forEach(category => {
        const state = this.createState(category);
        this.states.set(category, state);
        if (!state.initialized) { if (this.observer) this.observer.observe(category); else this.load(category); }
      });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.states?.forEach(state => state.request?.abort());
      this.dialog?.close();
      this.closePlayer();
      this.controller?.abort();
      this.controller = null;
    }
    onClick(event) {
      const play = event.target.closest('[data-video-play]');
      if (play && this.contains(play)) { event.preventDefault(); this.openPlayer(play); return; }
      if (event.target.closest('[data-video-close]')) { this.dialog?.close(); return; }
      const load = event.target.closest('[data-video-load]');
      if (load) { this.load(load.closest('[data-video-category]')); return; }
      const jump = event.target.closest('a[href^="#videos-"]');
      if (jump) {
        const category = this.querySelector(jump.getAttribute('href'));
        if (category && !this.states.get(category)?.initialized) this.load(category);
      }
    }
    feedURL(value) {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin) throw new Error('Unexpected feed origin');
      url.searchParams.set('section_id', this.dataset.feedSection || 'labchest-video-feed');
      for (const key of ['preview_theme_id', 'preview_key']) {
        const preview = new URL(location.href).searchParams.get(key);
        if (preview) url.searchParams.set(key, preview);
      }
      return url;
    }
    updateButton(category, state, failed = false) {
      const button = category.querySelector('[data-video-load]');
      if (!button) return;
      button.hidden = !failed && (!state.initialized || !state.next);
      button.disabled = state.loading;
      button.textContent = state.loading ? this.dataset.loading : failed ? this.dataset.retry : this.dataset.showMore;
      button.setAttribute('aria-busy', String(state.loading));
    }
    createState(category, previous) {
      const empty = category.dataset.videoEmptyLibrary === 'true';
      const state = { loading: false, initialized: empty, next: '', url: category.dataset.feedUrl || '', ids: new Set(), cache: previous?.cache || new Map(), total: previous?.total ?? (empty ? 0 : null), pageSize: previous?.pageSize || 6, shown: 0, request: null };
      const feed = !previous && category.querySelector('[data-video-feed]');
      if (feed) {
        const total = Number(feed.dataset.total);
        const pageSize = Number(feed.dataset.pageSize);
        if (Number.isInteger(total) && total >= 0 && Number.isInteger(pageSize) && pageSize > 0) {
          state.total = total;
          state.pageSize = pageSize;
          state.cache.set(Number(feed.dataset.page), feed.cloneNode(true));
        }
      }
      return state;
    }
    changeOrder() {
      this.order = this.sort.value === 'newest' ? 'newest' : 'oldest';
      this.dialog?.close();
      this.states.forEach((previous, category) => {
        previous.request?.abort();
        const state = this.createState(category, previous);
        this.states.set(category, state);
        category.querySelector('[data-video-status]').textContent = '';
        if (!state.initialized) {
          category.querySelector('[data-video-content]').replaceChildren();
          category.removeAttribute('data-enhanced');
          const fallback = category.querySelector('[data-video-fallback]');
          if (fallback) fallback.hidden = false;
          this.updateButton(category, state);
          if (this.observer) this.observer.observe(category); else this.load(category);
        }
      });
    }
    async fetchPage(category, state, page, signal) {
      if (state.cache.has(page)) return state.cache.get(page);
      const url = this.feedURL(state.url);
      url.searchParams.set('page', String(page));
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Feed HTTP ${response.status}`);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      signal.throwIfAborted();
      const feed = parsed.querySelector('[data-video-feed]');
      if (!feed || feed.dataset.blog !== this.dataset.blog || feed.dataset.tag !== category.dataset.tag || Number(feed.dataset.page) !== page) throw new Error('Unexpected feed');
      const total = Number(feed.dataset.total);
      const pageSize = Number(feed.dataset.pageSize);
      if (!Number.isInteger(total) || total < 0 || !Number.isInteger(pageSize) || pageSize < 1) throw new Error('Invalid pagination');
      if (state.total !== null && (state.total !== total || state.pageSize !== pageSize)) {
        state.cache.clear();
        state.total = null;
        state.shown = 0;
        state.ids.clear();
        state.initialized = false;
        state.next = '';
        throw new Error('Archive changed during pagination');
      }
      state.total = total;
      state.pageSize = pageSize;
      state.cache.set(page, feed);
      return feed;
    }
    async load(category) {
      const state = this.states.get(category);
      if (!state || state.loading || (state.initialized && !state.next)) return;
      state.loading = true;
      const order = this.order;
      const status = category.querySelector('[data-video-status]');
      const button = category.querySelector('[data-video-load]');
      const restoreFocus = document.activeElement === button;
      let firstAdded;
      status.textContent = this.dataset.loading;
      category.setAttribute('aria-busy', 'true');
      this.updateButton(category, state);
      let failed = false;
      const timeout = new AbortController();
      state.request = timeout;
      const timer = setTimeout(() => timeout.abort(), 15000);
      const stop = () => timeout.abort();
      this.controller.signal.addEventListener('abort', stop, { once: true });
      try {
        if (state.total === null) await this.fetchPage(category, state, 1, timeout.signal);
        const count = Math.min(6, state.total - state.shown);
        const batch = [];
        for (let offset = 0; offset < count; offset++) {
          const rank = state.shown + offset;
          const index = order === 'oldest' ? state.total - 1 - rank : rank;
          const page = Math.floor(index / state.pageSize) + 1;
          const feed = await this.fetchPage(category, state, page, timeout.signal);
          const card = feed.querySelectorAll('[data-video-id]')[index % state.pageSize];
          if (!card) throw new Error('Missing video in page');
          batch.push(card.cloneNode(true));
        }
        if (this.states.get(category) !== state || timeout.signal.aborted) return;
        if (!state.initialized) {
          const feed = state.cache.values().next().value.cloneNode(true);
          feed.querySelector('[data-video-grid]').replaceChildren();
          category.querySelector('[data-video-content]').replaceChildren(feed);
        }
        const grid = category.querySelector('[data-video-grid]');
        let added = 0;
        batch.forEach(card => {
          if (!state.ids.has(card.dataset.videoId)) {
            grid.append(card);
            state.ids.add(card.dataset.videoId);
            firstAdded ||= card;
            added++;
          }
        });
        state.shown += count;
        state.initialized = true;
        state.next = state.shown < state.total ? 'more' : '';
        category.dataset.enhanced = '';
        const fallback = category.querySelector('[data-video-fallback]');
        if (fallback) fallback.hidden = true;
        status.textContent = added ? this.dataset.loaded.replace('[count]', String(added)) : '';
      } catch (error) {
        if (!this.controller?.signal.aborted && this.states.get(category) === state) { failed = true; status.textContent = this.dataset.error; }
      } finally {
        clearTimeout(timer);
        this.controller?.signal.removeEventListener('abort', stop);
        if (this.states.get(category) === state) {
          state.loading = false;
          state.request = null;
          category.setAttribute('aria-busy', 'false');
          this.updateButton(category, state, failed);
          if (restoreFocus && button?.hidden) {
            const target = firstAdded?.querySelector('[data-video-play], a') || status;
            if (target === status) status.tabIndex = -1;
            target.focus({ preventScroll: true });
          }
        }
      }
    }
    openPlayer(button) {
      const url = LabChestVideoLibrary.instagramURL(button.dataset.videoUrl);
      if (!url || !this.dialog) {
        let notice = button.closest('.lc-video-card').querySelector('[data-video-invalid]');
        if (!notice) { notice = document.createElement('p'); notice.dataset.videoInvalid = ''; notice.setAttribute('role', 'status'); button.closest('.lc-video-card').append(notice); }
        notice.textContent = this.dataset.invalid;
        return;
      }
      this.playerTrigger = button;
      this.dialog.querySelector('[data-video-dialog-title]').textContent = button.dataset.videoTitle;
      this.dialog.querySelector('[data-video-original]').href = url.original;
      this.dialog.querySelector('[data-video-player-status]').textContent = this.dataset.playerHint;
      const frame = document.createElement('iframe');
      frame.title = button.dataset.videoTitle;
      frame.src = url.embed;
      frame.allow = 'encrypted-media; picture-in-picture; fullscreen';
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.addEventListener('error', () => { this.dialog.querySelector('[data-video-player-status]').textContent = this.dataset.playerError; }, { signal: this.controller.signal });
      this.dialog.querySelector('[data-video-media]').replaceChildren(frame);
      this.dialog.showModal();
    }
    closePlayer() {
      this.dialog?.querySelector('[data-video-media]')?.replaceChildren();
      if (this.playerTrigger?.isConnected) this.playerTrigger.focus({ preventScroll: true });
      this.playerTrigger = null;
    }
  }
  customElements.define('labchest-video-library', LabChestVideoLibrary);
})();
